import { CustomUnit, Unit } from '@mitsubishi/shared/data-access';

import { Selector, createSelector } from '@ngxs/store';

import { Alternative, Selection } from '../../models';
import { ProjectSelectionsSelectors } from '../selection/selections.selectors';
import { PROJECT_ALTERNATIVE_STATE_TOKEN } from './alternative.model';

export class AlternativeSelectors {
  @Selector([PROJECT_ALTERNATIVE_STATE_TOKEN, ProjectSelectionsSelectors.getActiveSelectionBaseAlternative])
  static getSelectedAlternativeUuid(selectedAlternativeId: string | null, baseAlternative: Alternative | null) {
    return selectedAlternativeId ? selectedAlternativeId : baseAlternative ? baseAlternative.uuid : '';
  }

  @Selector([ProjectSelectionsSelectors.getActiveSelection, AlternativeSelectors.getSelectedAlternativeUuid])
  static getSelectedAlternative(selection: Selection | undefined, selectedAlternativeId: string) {
    return selection?.alternatives[selectedAlternativeId] || selection?.base;
  }

  @Selector([
    AlternativeSelectors.getSelectedAlternativeUuid,
    ProjectSelectionsSelectors.getActiveSelectionUnits,
    ProjectSelectionsSelectors.getActiveSelection,
  ])
  static getSelectedAlternativeUnits(
    alternativeUuid: string,
    units: (Unit | CustomUnit)[],
    selection: Selection | undefined
  ) {
    if (!selection) {
      return [];
    }
    const alternative = selection.alternatives[alternativeUuid] || selection.base;
    return units
      .filter(unit => {
        return (
          !(unit as Unit).isExternalUnit &&
          (alternative.units[unit.uuid] != null || alternative.unitsCustom[unit.uuid] != null)
        );
      })
      .sort((a, b) => {
        // Sort by position from unit_alternative, not from unit
        const positionA = alternative.units[a.uuid]?.position || alternative.unitsCustom[a.uuid]?.position || 0;
        const positionB = alternative.units[b.uuid]?.position || alternative.unitsCustom[b.uuid]?.position || 0;
        return positionA - positionB;
      });
  }

  @Selector([
    AlternativeSelectors.getSelectedAlternativeUuid,
    ProjectSelectionsSelectors.getActiveQuoteDeletedUnits,
    ProjectSelectionsSelectors.getActiveSelection,
  ])
  static getSelectedAlternativeDeletedUnits(
    alternativeUuid: string,
    units: (Unit | CustomUnit)[],
    selection: Selection | undefined
  ) {
    if (!selection) {
      return [];
    }
    const alternative = selection.alternatives[alternativeUuid] || selection.base;

    return units
      .filter(unit => alternative.units[unit.uuid] || alternative.unitsCustom[unit.uuid])
      .filter(unit => !unit.isExternalUnit)
      .sort((a, b) => {
        // Sort by position from unit_alternative, not from unit
        const positionA = alternative.units[a.uuid]?.position || alternative.unitsCustom[a.uuid]?.position || 0;
        const positionB = alternative.units[b.uuid]?.position || alternative.unitsCustom[b.uuid]?.position || 0;
        return positionA - positionB;
      });
  }

  static getSelectedAlternativeUnit(unitId: string) {
    return createSelector(
      [AlternativeSelectors.getSelectedAlternativeUuid, ProjectSelectionsSelectors.getActiveSelection],
      (alternativeUuid: string, selection: Selection) => {
        const alternative = selection?.alternatives[alternativeUuid] || selection?.base;
        if (!alternative) {
          return undefined;
        }

        return alternative?.units[unitId] || alternative?.unitsCustom[unitId];
      }
    );
  }

  static getAlternativeUnit(alternativeId: string, unitId: string) {
    return createSelector([ProjectSelectionsSelectors.getActiveSelection], (selection: Selection) => {
      const alternative = selection.alternatives[alternativeId] || selection.base;
      return alternative.units[unitId] || alternative.unitsCustom[unitId];
    });
  }
}
